import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { applyTypography, BaseButton, Toast, useAuth, useT } from '@repo/common';
import { ReactComponent as NavigationLeftIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as PhoneIcon16 } from '@repo/common/assets/icons/16px/icon_16px_phone_filled.svg';
import { ReactComponent as MailIcon16 } from '@repo/common/assets/icons/16px/icon_16px_email_filled.svg';
import { ReactComponent as KakaoIcon16 } from '@repo/common/assets/icons/16px/icon_16px_kakao_simple.svg';
import RightArrowIcon from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import PaymentSummary from '../../components/Kpathport/Booking/PaymentSummary';
import RefundDetailsButton from '../../components/Kpathport/Booking/RefundDetailsButton';
import DateSelectionModal from '../../components/Kpathport/DateSelectionModal';
import StepIndicator from '../../components/Kpathport/components/StepIndicator';
import RefundModal from '../../components/Kpathport/Modal/RefundModal';
import {
  formatDate,
  formatTime,
} from '../../components/Kpathport/utils/dateTimeUtils';
import { transformDateSlots } from '../../components/Kpathport/utils/dateSlotUtils';
import { useGoogleMap } from '../../components/Kpathport/hooks/useGoogleMap';
import { getCartItems, updateCartItemDateSlot } from '../../api/cart/cart.api';
import { createBookings } from '../../api/booking/booking.api';
import {
  buildAnswersPayload,
  clearIntakeAnswers,
} from '../../components/Kpathport/utils/intakeAnswers';
import { getWorkshopDetail } from '../../api/workshop/workshop.api';
import { combineRefundPolicies } from '../../components/Kpathport/utils/refundPolicyUtils';
import { formatWorkshopPrice } from '../../components/Kpathport/utils/priceUtils';

const WorkshopLocation = ({ meetingPoint }) => {
  const hasCoordinates =
    Number.isFinite(meetingPoint?.latitude) &&
    Number.isFinite(meetingPoint?.longitude);
  const { mapRef, mapError } = useGoogleMap(
    meetingPoint?.address || '',
    hasCoordinates
      ? { lat: meetingPoint.latitude, lng: meetingPoint.longitude }
      : null,
  );

  return (
    <>
      <div className='h-[155px] overflow-clip relative rounded-[15px] shrink-0 w-full tabletH:w-[340px]'>
        {mapError ? (
          <div className='flex items-center justify-center h-full bg-grayscale-gray01 rounded-[15px]'>
            <p className={`${applyTypography('b14')} text-text-placeholder`}>
              {mapError}
            </p>
          </div>
        ) : (
          <div className='relative w-full h-full rounded-[15px] overflow-hidden'>
            <div
              ref={mapRef}
              className='w-full h-full rounded-[15px]'
              style={{ minHeight: '155px' }}
            />
          </div>
        )}
      </div>
      <div className='flex flex-col items-start max-w-[700px] relative shrink-0 w-full'>
        <div className='flex items-center justify-center pb-[2px] tabletH:pb-[4px] pt-0 px-0 relative shrink-0 w-full'>
          <p
            className={`flex-1 ${applyTypography(
              'krDefaultStrong',
            )} text-text-tertiary shrink-0 whitespace-pre-wrap`}
            style={{ lineHeight: '1.5' }}
          >
            {meetingPoint?.name || ''}
          </p>
        </div>
        <p
          className={`${applyTypography(
            'b14',
          )} text-text-tertiary shrink-0 w-full whitespace-pre-wrap`}
          style={{ lineHeight: '1.4' }}
        >
          {meetingPoint?.address || ''}
        </p>
      </div>
    </>
  );
};

// 개최자(호스트) 문의 연락처. 워크숍 상세의 inquiryContact 를 그대로 보여준다.
const ContactInfo = ({ mobile = false, contacts = [] }) => {
  const t = useT();

  // 여러 워크숍을 함께 결제하는 경우 워크숍별로 나눠 보여준다
  const validContacts = contacts.filter(
    (c) => c?.phone || c?.email || c?.openChatId,
  );
  const showTitles = validContacts.length > 1;

  const itemClass =
    'content-stretch flex gap-[8px] items-center pb-[8px] last:pb-0 relative rounded-[8px] shrink-0';
  const itemTypo = `${applyTypography('b13Strong')} text-text-tertiary shrink-0`;

  return (
    <div
      className={`bg-grayscale-gray01 border border-grayscale-gray02 rounded-[15px] shrink-0 w-full ${
        mobile
          ? 'content-stretch flex flex-col gap-[8px] items-start overflow-clip p-[16px]'
          : 'flex items-start justify-between overflow-clip pl-[16px] pr-[16px] py-[16px]'
      }`}
    >
      <div
        className={`content-stretch flex ${
          mobile ? 'items-start w-full' : 'items-center justify-center pt-[8px]'
        } relative shrink-0`}
      >
        <p
          className={`${mobile ? 'flex-[1_0_0]' : ''} ${applyTypography(
            mobile ? 'b16Strong' : 'b18Strong',
          )} text-text-primary shrink-0 whitespace-pre-wrap`}
          style={{ lineHeight: '1.5' }}
        >
          {t('Need our help?', '도움이 필요하신가요?')}
        </p>
      </div>

      <div
        className={`bg-grayscale-gray00 flex flex-col gap-[8px] items-start justify-center p-[16px] relative shrink-0 ${
          mobile ? 'rounded-[10px] w-full' : 'rounded-[15px] w-[300px]'
        }`}
      >
        {validContacts.length === 0 ? (
          <p
            className={`${applyTypography('b13')} text-text-secondary`}
            style={{ lineHeight: '1.4' }}
          >
            {t(
              'Contact information has not been added yet.',
              '아직 등록된 연락처 정보가 없습니다.',
            )}
          </p>
        ) : (
          validContacts.map((contact, index) => (
            <div
              key={`${contact.workshopTitle ?? ''}-${index}`}
              className='flex flex-col items-start w-full'
            >
              {showTitles && contact.workshopTitle && (
                <p
                  className={`${applyTypography(
                    'b12Strong',
                  )} text-text-secondary pb-[6px] w-full truncate`}
                  style={{ lineHeight: '1.4' }}
                >
                  {contact.workshopTitle}
                </p>
              )}

              {contact.phone && (
                <div className={itemClass}>
                  <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 size-[16px] text-grayscale-gray07'>
                    <PhoneIcon16 className='w-4 h-4' />
                  </div>
                  <a
                    href={`tel:${contact.phone}`}
                    className={`${itemTypo} hover:underline`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {contact.phone}
                  </a>
                </div>
              )}

              {contact.email && (
                <div className={itemClass}>
                  <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 size-[16px] text-grayscale-gray07'>
                    <MailIcon16 className='w-4 h-4' />
                  </div>
                  <a
                    href={`mailto:${contact.email}`}
                    className={`${itemTypo} hover:underline break-all`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {contact.email}
                  </a>
                </div>
              )}

              {contact.openChatId && (
                <div className={itemClass}>
                  <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 size-[16px] text-grayscale-gray07'>
                    <KakaoIcon16 className='w-4 h-4' />
                  </div>
                  <p className={itemTypo} style={{ lineHeight: '1.4' }}>
                    {contact.openChatId}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const CheckDetails = () => {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentStep] = useState(2);
  const [cartItems, setCartItems] = useState([]);
  const [workshopItems, setWorkshopItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [editingCartItemId, setEditingCartItemId] = useState(null);
  const [dateSlots, setDateSlots] = useState([]);
  const [showDateUpdatedToast, setShowDateUpdatedToast] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showThingsToKnow, setShowThingsToKnow] = useState(false);
  const [isSubmittingFreeBooking, setIsSubmittingFreeBooking] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const cartItemIdsParam = searchParams.get('cartItemIds');

        let selectedItems = [];
        if (cartItemIdsParam) {
          const cartItemIds = cartItemIdsParam
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id));
          const cartData = await getCartItems();
          selectedItems = cartData.filter((item) =>
            cartItemIds.includes(item.id),
          );
        } else {
          const cartData = await getCartItems();
          selectedItems = cartData.filter((item) => item.isSelected);
        }

        if (selectedItems.length > 0) {
          setCartItems(selectedItems);

          const workshopPromises = selectedItems.map(async (item) => {
            if (!item.workshopId) return null;

            const workshop = await getWorkshopDetail(item.workshopId);
            let selectedDateSlot = null;
            if (item.workshopDateSlotId && workshop.dateSlots) {
              selectedDateSlot = workshop.dateSlots.find(
                (s) => s.id === item.workshopDateSlotId,
              );
            }

            return {
              cartItemId: item.id,
              workshop,
              selectedDateSlot,
            };
          });

          const workshopResults = await Promise.all(workshopPromises);
          setWorkshopItems(workshopResults.filter((item) => item !== null));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleChangeDate = (cartItemId) => {
    setEditingCartItemId(cartItemId);
    const workshopItem = workshopItems.find(
      (item) => item.cartItemId === cartItemId,
    );
    if (workshopItem?.workshop?.dateSlots) {
      setDateSlots(transformDateSlots(workshopItem.workshop.dateSlots));
    }
    setIsDateModalOpen(true);
  };

  const handleCloseDateModal = () => {
    setIsDateModalOpen(false);
    setEditingCartItemId(null);
  };

  const handleDateSelected = async (workshopDateSlotId) => {
    if (!editingCartItemId) return;

    try {
      await updateCartItemDateSlot(editingCartItemId, workshopDateSlotId);

      setWorkshopItems((prevItems) =>
        prevItems.map((item) => {
          if (
            item.cartItemId === editingCartItemId &&
            item.workshop?.dateSlots
          ) {
            const slot = item.workshop.dateSlots.find(
              (s) => s.id === workshopDateSlotId,
            );
            return {
              ...item,
              selectedDateSlot: slot,
            };
          }
          return item;
        }),
      );

      const cartData = await getCartItems();
      const cartItemIdsParam = searchParams.get('cartItemIds');

      if (cartItemIdsParam) {
        const cartItemIds = cartItemIdsParam
          .split(',')
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !isNaN(id));
        setCartItems(cartData.filter((item) => cartItemIds.includes(item.id)));
      } else {
        setCartItems(cartData.filter((item) => item.isSelected));
      }

      setIsDateModalOpen(false);
      setEditingCartItemId(null);
      setShowDateUpdatedToast(true);
    } catch (error) {
      console.error('Failed to update cart item date slot:', error);
      alert('날짜를 변경하는데 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handlePolicyClick = () => {
    setShowRefundModal(true);
  };

  const handleThingsToKnow = () => {
    setShowThingsToKnow(true);
  };

  // 워크숍별 Things to know / 호스트 연락처 (여러 건이면 워크숍명으로 구분)
  const isMultiWorkshop = workshopItems.length > 1;
  const thingsToKnowText = workshopItems
    .map(({ workshop }) => {
      const text = (workshop?.thingsToKnow || '').trim();
      if (!text) return '';
      return isMultiWorkshop ? `[${workshop?.title || ''}]\n${text}` : text;
    })
    .filter(Boolean)
    .join('\n\n');

  const hostContacts = workshopItems.map(({ workshop }) => ({
    workshopTitle: workshop?.title || '',
    phone: workshop?.inquiryContact?.phone || '',
    email: workshop?.inquiryContact?.email || '',
    openChatId: workshop?.inquiryContact?.openChatId || '',
  }));

  // 워크숍별 전용 정책. 여러 워크숍이면 제목을 붙여 합치고, 전용 정책이 없으면 기본 정책을 쓴다
  const refundPolicyText = combineRefundPolicies(
    workshopItems.map(({ workshop }) => ({
      workshopId: workshop?.id,
      title: workshop?.title,
      refundPolicy: workshop?.refundPolicy,
    })),
  );

  // 계좌이체 워크숍이면 다음 단계가 카드 결제가 아니라 계좌 안내다 — 버튼 라벨과 안내 문구를 바꾼다
  const isBankTransferOrder = cartItems.some(
    (item) =>
      !item.isFree &&
      String(item.paymentMethod || '').toUpperCase() === 'BANK_TRANSFER',
  );
  const bankTransferNote = isBankTransferOrder
    ? t(
        'This workshop is paid by bank transfer. You will get the account details in the next step.',
        '이 워크숍은 계좌이체로 결제합니다. 다음 단계에서 입금 계좌를 안내드립니다.',
      )
    : '';

  const paymentInfo = {
    total: cartItems.reduce((sum, item) => {
      const price = item.isFree ? 0 : item.price ? parseFloat(item.price) : 0;
      return sum + price;
    }, 0),
    originalPrice: null,
    itemCount: cartItems.length,
    // 계좌이체 워크숍은 KRW, 카드 결제는 USD — 첫 유료 항목의 통화를 따른다
    currency:
      (cartItems.find((item) => !item.isFree) || cartItems[0])?.currency ||
      'USD',
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-grayscale-gray01'>
        <p className={`${applyTypography('h5')} text-text-primary`}>
          로딩 중...
        </p>
      </div>
    );
  }

  if (workshopItems.length === 0 || cartItems.length === 0) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-grayscale-gray01'>
        <p className={`${applyTypography('h5')} text-text-primary`}>
          데이터를 불러올 수 없습니다.
        </p>
      </div>
    );
  }

  const handlePayNow = async () => {
    if (isSubmittingFreeBooking) return;
    const cartItemIds = cartItems.map((item) => item.id);
    const allSelectedItemsAreFree = cartItems.every((item) => {
      if (item.isFree) return true;
      const numericPrice =
        typeof item.price === 'number'
          ? item.price
          : parseFloat(String(item.price || '').replace(/[^0-9.-]/g, ''));
      return !Number.isNaN(numericPrice) && numericPrice <= 0;
    });

    if (allSelectedItemsAreFree) {
      try {
        setIsSubmittingFreeBooking(true);
        const customerName =
          user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : user?.nickname || user?.email || '';
        const customerEmail = user?.email || user?.socialEmail || '';
        // 장바구니 화면에서 받아둔 사전 질문 답변을 함께 보낸다
        const answerWorkshopIds = [
          ...new Set(
            (cartItems ?? []).map((item) => item.workshopId).filter(Boolean),
          ),
        ];
        const bookingIds = await createBookings(
          cartItemIds,
          customerName,
          customerEmail,
          { answers: buildAnswersPayload(answerWorkshopIds) },
        );
        clearIntakeAnswers(answerWorkshopIds);

        if (!Array.isArray(bookingIds) || bookingIds.length === 0) {
          alert(
            t(
              'Failed to create booking. Please try again.',
              '예약을 생성하지 못했습니다. 다시 시도해주세요.',
            ),
          );
          return;
        }

        navigate(
          `/community/kpathport/confirmed?bookingIds=${bookingIds.join(',')}`,
        );
        return;
      } catch (error) {
        console.error(
          'Failed to create free booking:',
          error?.response?.data || error,
        );
        alert(
          t(
            'Failed to complete booking. Please try again.',
            '예약을 완료하지 못했습니다. 다시 시도해주세요.',
          ),
        );
      } finally {
        setIsSubmittingFreeBooking(false);
      }
    }

    navigate(
      `/community/kpathport/payments?cartItemIds=${cartItemIds.join(',')}`,
    );
  };

  const totalNumericValue =
    typeof paymentInfo.total === 'number'
      ? paymentInfo.total
      : parseFloat(String(paymentInfo.total || '').replace(/[^0-9.-]/g, ''));
  const isFreeTotal =
    !Number.isNaN(totalNumericValue) && totalNumericValue <= 0;
  const formattedMobileTotal = isFreeTotal
    ? t('Free', '무료')
    : t(
        formatWorkshopPrice(totalNumericValue, paymentInfo.currency),
        formatWorkshopPrice(totalNumericValue, paymentInfo.currency).replace(
          'won',
          '원',
        ),
      );

  return (
    <>
      <div className='hidden tabletH:flex content-start flex-wrap gap-[24px_0px] items-start justify-center pb-[240px] pl-[44px] pr-[100px] pt-[24px] relative size-full bg-grayscale-gray00 min-h-screen'>
        <div className='content-start flex flex-[1_0_0] flex-wrap gap-0 items-start max-w-[778px] min-h-px min-w-[600px] relative'>
          <div className='content-stretch flex h-[70px] items-center relative shrink-0'>
            <BaseButton
              type='text'
              size='s'
              onClick={handleBack}
              className='bg-grayscale-gray01 hover:bg-grayscale-gray01 min-w-0 border-0 p-0 content-stretch relative rounded-[50px] shrink-0 size-[35px] hover:opacity-80 transition-opacity'
              aria-label={t('Go back', '뒤로 가기')}
              icon={
                <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 w-[20px]'>
                  <NavigationLeftIcon20 className='w-full h-full' />
                </div>
              }
            />
          </div>

          <div className='content-stretch flex flex-[1_0_0] flex-col gap-[24px] items-start min-h-px min-w-[520px] pl-[24px] relative'>
            <div className='content-stretch flex h-[70px] items-center justify-between overflow-clip py-[16px] relative shrink-0 w-full'>
              <p
                className={`${applyTypography(
                  'h5',
                )} text-text-primary shrink-0`}
                style={{ lineHeight: '1.2' }}
              >
                {t('Check details', '상세 확인')}
              </p>
              <StepIndicator currentStep={currentStep} />
            </div>

            {workshopItems.map((workshopItem) => {
              const workshop = workshopItem.workshop;
              const selectedDateSlot = workshopItem.selectedDateSlot;
              const cartItemId = workshopItem.cartItemId;

              const workshopDate = selectedDateSlot?.date
                ? formatDate(selectedDateSlot.date)
                : '';
              const workshopTime =
                selectedDateSlot?.startTime && selectedDateSlot?.endTime
                  ? `${formatTime(selectedDateSlot.startTime)} - ${formatTime(
                      selectedDateSlot.endTime,
                    )}`
                  : '';

              return (
                <div
                  key={cartItemId}
                  className='content-stretch flex flex-col items-start overflow-clip p-[24px] relative shrink-0 w-full'
                >
                  <div className='border-b border-grayscale-gray02 flex flex-col gap-[6px] items-start not-italic pb-[16px] pt-0 px-0 relative shrink-0 w-full'>
                    <p
                      className={`${applyTypography(
                        'title1',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.2' }}
                    >
                      {workshop.title || t('Workshop name', '워크숍 이름')}
                    </p>
                    <div className='relative flex flex-col items-start gap-0 shrink-0'>
                      <p
                        className={`${applyTypography(
                          'b12',
                        )} text-text-secondary shrink-0`}
                        style={{ lineHeight: '1.4' }}
                      >
                        {t(
                          'No cancellations or refunds on the workshop day.',
                          '워크숍 당일에는 취소 및 환불이 불가합니다.',
                        )}
                        <br />
                        {t(
                          'Cancellation fees apply from 7 days before.',
                          '7일 전부터 취소 수수료가 부과됩니다.',
                        )}{' '}
                        <BaseButton
                          type='text'
                          size='s'
                          htmlType='button'
                          hideIcon
                          typo={false}
                          isStatic
                          onClick={handlePolicyClick}
                          textWrapperClass='h-auto'
                          className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 align-baseline text-inherit underline underline-offset-[14%] hover:opacity-80 transition-opacity'
                        >
                          {t('See our policy', '정책 보기')}
                        </BaseButton>{' '}
                        {t('for details.', '에서 자세한 내용을 확인하세요.')}
                      </p>
                    </div>
                  </div>

                  <div className='border-b border-grayscale-gray02 flex flex-col gap-[6px] items-start px-0 py-[16px] relative shrink-0 w-full'>
                    <p
                      className={`${applyTypography(
                        'b16Strong',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.5' }}
                    >
                      {t('Date', '날짜')}
                    </p>
                    <div className='flex gap-[8px] items-center relative shrink-0 w-full'>
                      <div className='flex flex-[1_0_0] flex-col items-start min-h-px min-w-px not-italic relative shrink-0'>
                        <p
                          className={`${applyTypography(
                            'b14',
                          )} text-text-primary shrink-0 w-full`}
                          style={{ lineHeight: '1.4' }}
                        >
                          {workshopDate}
                          <br />
                          {workshopTime}
                        </p>
                      </div>
                      <BaseButton
                        type='text'
                        size='s'
                        hideIcon
                        typo={false}
                        isStatic
                        onClick={() => handleChangeDate(cartItemId)}
                        textWrapperClass='h-auto flex-none'
                        className='bg-grayscale-gray01 h-auto min-w-0 cursor-pointer border-0 px-[12px] py-[6px] relative rounded-[8px] shrink-0 hover:opacity-80 transition-opacity'
                      >
                        <p
                          className={`${applyTypography(
                            'b13Strong',
                          )} text-text-tertiary shrink-0 text-center`}
                          style={{ lineHeight: '1.4' }}
                        >
                          {t('Change date', '날짜 변경')}
                        </p>
                      </BaseButton>
                    </div>
                  </div>

                  {workshop.meetingPoint && (
                    <div className='flex flex-col gap-[8px] items-start px-0 py-[16px] relative shrink-0 w-full'>
                      <p
                        className={`${applyTypography(
                          'b16Strong',
                        )} text-text-primary shrink-0`}
                        style={{ lineHeight: '1.5' }}
                      >
                        {t('Location', '장소')}
                      </p>
                      <WorkshopLocation meetingPoint={workshop.meetingPoint} />
                    </div>
                  )}
                </div>
              );
            })}

            <div
              className='bg-grayscale-gray01 border border-grayscale-gray02 flex items-center overflow-clip p-[24px] relative rounded-[15px] shrink-0 w-full hover:opacity-80 transition-opacity cursor-pointer'
              onClick={handleThingsToKnow}
            >
              <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px not-italic relative shrink-0'>
                <p
                  className={`${applyTypography(
                    'b18Strong',
                  )} text-text-primary shrink-0 w-full`}
                  style={{ lineHeight: '1.5' }}
                >
                  {t('Things to know', '알아두세요')}
                </p>
                <p
                  className={`${applyTypography(
                    'b12',
                  )} text-text-secondary shrink-0 w-full`}
                  style={{ lineHeight: '1.4' }}
                >
                  {t('Please review before booking', '예약 전에 꼭 확인해주세요')}
                </p>
              </div>
              <div className='overflow-clip relative shrink-0 size-[16px]'>
                <img
                  alt=''
                  className='block max-w-none size-full'
                  src={RightArrowIcon}
                />
              </div>
            </div>

            <ContactInfo contacts={hostContacts} />
          </div>
        </div>

        <div className='content-stretch flex flex-col gap-[24px] items-center pl-[24px] pt-[70px] relative shrink-0 w-[444px]'>
          <PaymentSummary
            total={paymentInfo.total}
            originalPrice={paymentInfo.originalPrice}
            itemCount={paymentInfo.itemCount}
            currency={paymentInfo.currency}
            onPayNow={handlePayNow}
            disabled={isSubmittingFreeBooking}
            loading={isSubmittingFreeBooking}
            onRefundDetails={() => setShowRefundModal(true)}
            showRefundDetailsLink
            payLabel={isBankTransferOrder ? t('Continue', '계속하기') : undefined}
            note={bankTransferNote}
          />
        </div>
      </div>

      <div className='w-full min-h-screen tabletH:hidden bg-grayscale-gray00'>
        <div className='bg-grayscale-gray01 px-[16px] pt-[16px] pb-[8px]'>
          <div className='relative flex items-center justify-between'>
            <BaseButton
              type='text'
              size='s'
              onClick={handleBack}
              className='left-0 bg-grayscale-gray01 hover:bg-grayscale-gray01 h-auto min-w-0 border-0 content-stretch p-[14px] relative rounded-[8px] shrink-0 hover:opacity-80 transition-opacity'
              aria-label={t('Go back', '뒤로 가기')}
              icon={
                <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 w-[20px]'>
                  <NavigationLeftIcon20 className='w-full h-full' />
                </div>
              }
            />
            <div className='flex flex-col items-center gap-[8px]'>
              <p
                className={`${applyTypography(
                  'title3',
                )} text-text-primary shrink-0`}
                style={{ lineHeight: '1.2' }}
              >
                {t('Check details', '상세 확인')}
              </p>
              <StepIndicator
                currentStep={currentStep}
                className='scale-[0.92] origin-center'
              />
            </div>
            <div className='w-4 h-4'></div>
          </div>
        </div>

        <div className='content-stretch flex flex-col gap-[16px] items-center overflow-clip px-[24px] pb-[24px] pt-[16px] relative shrink-0 w-full'>
          <div className='content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full'>
            {workshopItems.map((workshopItem) => {
              const workshop = workshopItem.workshop;
              const selectedDateSlot = workshopItem.selectedDateSlot;
              const cartItemId = workshopItem.cartItemId;

              const workshopDate = selectedDateSlot?.date
                ? formatDate(selectedDateSlot.date)
                : '';
              const workshopTime =
                selectedDateSlot?.startTime && selectedDateSlot?.endTime
                  ? `${formatTime(selectedDateSlot.startTime)} - ${formatTime(
                      selectedDateSlot.endTime,
                    )}`
                  : '';

              return (
                <div
                  key={`mobile-${cartItemId}`}
                  className='relative flex flex-col items-start w-full content-stretch overflow-clip shrink-0'
                >
                  <div className='border-b border-grayscale-gray02 flex flex-col gap-[6px] items-start not-italic pb-[16px] relative shrink-0 w-full'>
                    <p
                      className={`${applyTypography(
                        'title1',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.2' }}
                    >
                      {workshop.title || t('Workshop name', '워크숍 이름')}
                    </p>
                    <div className='relative flex flex-col items-start w-full gap-0 shrink-0'>
                      <p
                        className={`${applyTypography(
                          'b12',
                        )} text-text-secondary shrink-0 w-full whitespace-pre-wrap`}
                        style={{ lineHeight: '1.4' }}
                      >
                        {t(
                          'No cancellations or refunds on the workshop day.',
                          '워크숍 당일에는 취소 및 환불이 불가합니다.',
                        )}
                        <br />
                        {t(
                          'Cancellation fees apply from 7 days before.',
                          '7일 전부터 취소 수수료가 부과됩니다.',
                        )}{' '}
                        <BaseButton
                          type='text'
                          size='s'
                          htmlType='button'
                          hideIcon
                          typo={false}
                          isStatic
                          onClick={handlePolicyClick}
                          textWrapperClass='h-auto'
                          className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 align-baseline text-inherit underline underline-offset-[8%] hover:opacity-80 transition-opacity'
                        >
                          {t('See our policy', '정책 보기')}
                        </BaseButton>{' '}
                        {t('for details.', '에서 자세한 내용을 확인하세요.')}
                      </p>
                    </div>
                  </div>

                  <div className='border-b border-grayscale-gray02 flex flex-col gap-[6px] items-start pb-[16px] pt-[8px] relative shrink-0 w-full'>
                    <p
                      className={`${applyTypography(
                        'b16Strong',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.5' }}
                    >
                      {t('Date', '날짜')}
                    </p>
                    <div className='content-stretch flex gap-[8px] items-center relative shrink-0 w-full'>
                      <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px not-italic relative'>
                        <p
                          className={`${applyTypography(
                            'b14',
                          )} text-text-primary shrink-0 w-full whitespace-pre-wrap`}
                          style={{ lineHeight: '1.4' }}
                        >
                          {workshopDate}
                          <br />
                          {workshopTime}
                        </p>
                      </div>
                      <BaseButton
                        type='text'
                        size='s'
                        hideIcon
                        typo={false}
                        isStatic
                        onClick={() => handleChangeDate(cartItemId)}
                        textWrapperClass='h-auto flex-none'
                        className='bg-grayscale-gray01 h-auto min-w-0 cursor-pointer border-0 content-stretch px-[12px] py-[6px] relative rounded-[8px] shrink-0 hover:opacity-80 transition-opacity'
                      >
                        <p
                          className={`${applyTypography(
                            'b13Strong',
                          )} text-text-tertiary shrink-0 text-center`}
                          style={{ lineHeight: '1.4' }}
                        >
                          {t('Change date', '날짜 변경')}
                        </p>
                      </BaseButton>
                    </div>
                  </div>

                  {workshop.meetingPoint && (
                    <div className='content-stretch flex flex-col gap-[8px] items-start pb-[16px] pt-[8px] relative shrink-0 w-full'>
                      <p
                        className={`${applyTypography(
                          'b16Strong',
                        )} text-text-primary shrink-0`}
                        style={{ lineHeight: '1.5' }}
                      >
                        {t('Location', '장소')}
                      </p>
                      <WorkshopLocation meetingPoint={workshop.meetingPoint} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className='bg-grayscale-gray01 border border-grayscale-gray02 content-stretch flex items-center overflow-clip p-[16px] relative rounded-[15px] shrink-0 w-full hover:opacity-80 transition-opacity cursor-pointer'
            onClick={handleThingsToKnow}
          >
            <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px not-italic relative'>
              <p
                className={`${applyTypography(
                  'b16Strong',
                )} text-text-primary shrink-0 w-full`}
                style={{ lineHeight: '1.5' }}
              >
                Things to know
              </p>
              <p
                className={`${applyTypography(
                  'b12',
                )} text-text-secondary shrink-0 w-full`}
                style={{ lineHeight: '1.4' }}
              >
                Please review before booking
              </p>
            </div>
            <div className='overflow-clip relative shrink-0 size-[24px] flex items-center justify-center'>
              <img
                alt=''
                className='block h-[16px] w-[16px]'
                src={RightArrowIcon}
              />
            </div>
          </div>

          <ContactInfo mobile contacts={hostContacts} />

          <div className='content-stretch flex flex-col gap-[8px] items-center relative shrink-0 w-full'>
            <div className='border border-grayscale-gray02 content-stretch flex flex-col items-start overflow-clip p-[16px] relative rounded-[15px] shrink-0 w-full'>
              <div className='content-stretch flex items-center justify-center pb-[24px] relative shrink-0 w-full'>
                <p
                  className={`flex-[1_0_0] ${applyTypography(
                    'title3',
                  )} text-text-primary relative shrink-0 whitespace-pre-wrap`}
                  style={{ lineHeight: '1.2' }}
                >
                  {t('Payment Summary', '결제 요약')}
                </p>
              </div>
              <div className='border-t border-grayscale-gray02 content-stretch flex flex-col items-end justify-center py-[16px] relative shrink-0 w-full'>
                <div className='relative flex items-center justify-between w-full content-stretch shrink-0'>
                  <div className='relative flex flex-col items-start justify-center content-stretch shrink-0'>
                    <p
                      className={`${applyTypography(
                        'b14Strong',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.4' }}
                    >
                      {t('Total :', '총 결제 금액 :')}
                    </p>
                    <p
                      className={`${applyTypography(
                        'b12',
                      )} text-text-placeholder shrink-0`}
                      style={{ lineHeight: '1.4' }}
                    >
                      {t('(Incl. VAT)', '(부가세 포함)')}
                    </p>
                  </div>
                  <div className='relative flex flex-col items-end justify-center content-stretch shrink-0'>
                    <p
                      className={`${applyTypography('b18Strong')} ${
                        isFreeTotal ? 'text-text-accent' : 'text-text-primary'
                      } text-right shrink-0`}
                      style={{ lineHeight: '1.5' }}
                    >
                      {formattedMobileTotal}
                    </p>
                  </div>
                </div>
              </div>
              <BaseButton
                type='primary'
                size='l'
                hideIcon
                typo={false}
                onClick={handlePayNow}
                isDisabled={isSubmittingFreeBooking}
                disabledBgClass='bg-functional-success03'
                disabledBorderClass='border-transparent'
                textWrapperClass='h-auto flex-none'
                className={`bg-functional-success03 content-stretch h-[48px] min-w-0 border-0 px-[16px] py-[12px] relative rounded-[8px] shrink-0 w-full transition-opacity ${
                  isSubmittingFreeBooking
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:opacity-90 hover:bg-functional-success03'
                }`}
              >
                <p
                  className={`${applyTypography(
                    'b16Strong',
                  )} text-text-inverse text-center shrink-0`}
                  style={{ lineHeight: '1.5' }}
                >
                  {isSubmittingFreeBooking
                    ? t('Processing...', '처리 중...')
                    : isBankTransferOrder
                      ? t('Continue', '계속하기')
                      : t('Pay now', '결제하기')}
                </p>
              </BaseButton>
            </div>

            <div
              className='bg-grayscale-gray01 border border-grayscale-gray02 content-stretch flex items-center overflow-clip p-[24px] relative rounded-[15px] shrink-0 w-full hover:opacity-80 transition-opacity cursor-pointer'
              onClick={() => setShowRefundModal(true)}
            >
              <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px relative'>
                <p
                  className={`${applyTypography(
                    'b14Strong',
                  )} text-text-primary shrink-0 w-full whitespace-pre-wrap`}
                  style={{ lineHeight: '1.4' }}
                >
                  {t(
                    'View cancellation & refund details',
                    '취소 및 환불 정책 보기',
                  )}
                </p>
              </div>
              <div className='overflow-clip relative shrink-0 size-[24px] flex items-center justify-center'>
                <img
                  alt=''
                  className='block h-[16px] w-[16px]'
                  src={RightArrowIcon}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDateModalOpen && (
        <DateSelectionModal
          isOpen={isDateModalOpen}
          onClose={handleCloseDateModal}
          dateSlots={dateSlots}
          hasExistingBooking={false}
          onDateSelected={handleDateSelected}
          buttonText={t('Save changes', '변경 사항 저장')}
        />
      )}

      {showDateUpdatedToast && (
        <>
          <div className='hidden tabletH:fixed tabletH:bottom-[24px] tabletH:left-1/2 tabletH:-translate-x-1/2 tabletH:z-[1000] tabletH:block'>
            <Toast
              visibleOn='desktop'
              type='success'
              state='white'
              title={t('Date updated', '날짜가 변경되었습니다')}
              message=''
              onClose={() => setShowDateUpdatedToast(false)}
              hierarchy='important'
              direction='up'
              autoHideDelay={10000}
            />
          </div>

          <div className='tabletH:hidden fixed bottom-[24px] left-1/2 -translate-x-1/2 z-[9999999]'>
            <Toast
              visibleOn='mobile'
              type='success'
              state='white'
              title={t('Date updated', '날짜가 변경되었습니다')}
              message=''
              onClose={() => setShowDateUpdatedToast(false)}
              hierarchy='important'
              direction='up'
              autoHideDelay={10000}
            />
          </div>
        </>
      )}

      <RefundModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        content={refundPolicyText || ''}
      />

      <RefundModal
        isOpen={showThingsToKnow}
        onClose={() => setShowThingsToKnow(false)}
        title={t('Things to know', '알아두세요')}
        content={thingsToKnowText}
        emptyText={t(
          'No additional notes for this workshop.',
          '이 워크숍에 대한 추가 안내가 없습니다.',
        )}
      />
    </>
  );
};

export default CheckDetails;
